
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

// Toast wrapper to use toast inside error boundary
const ToastError = ({ error }: { error: string }) => {
  const { toast } = useToast();
  
  React.useEffect(() => {
    toast({
      title: "Error in component",
      description: error,
      variant: "destructive",
    });
  }, [error, toast]);
  
  return null;
};

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('Component error:', error);
    console.error('Component stack:', errorInfo.componentStack);
    
    // Call the onError prop if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return (
          <>
            {this.state.error && <ToastError error={this.state.error.message} />}
            {this.props.fallback}
          </>
        );
      }
      
      // Default fallback UI
      return (
        <div className="p-4 border rounded-md bg-red-50 text-red-800 dark:bg-red-950 dark:text-red-200 my-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5" />
            <h3 className="font-medium">Something went wrong</h3>
          </div>
          <p className="text-sm mb-2">
            {this.state.error?.message || 'An unexpected error occurred.'}
          </p>
          <pre className="text-xs bg-white/20 p-2 rounded overflow-auto max-h-[200px]">
            {this.state.error?.stack || 'No stack trace available'}
          </pre>
          {this.state.error && <ToastError error={this.state.error.message} />}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
